// Media health monitor: "the connection is up, is video actually reaching the
// viewer?"
//
// Why three signals and not one. The frames-decoded counter on its own lies in
// both directions: after a soft reconnect Epic's stats panel keeps reporting
// the old peer connection (60 fps, 529 kbps and "0 frames" in the same row —
// seen on 7 sessions on 2026-09-09), and older browsers never populated it at
// all. So a verdict here needs the <video> element's own frame signal, the
// inbound-rtp packet counters from getStats, and the player's lifecycle
// events to agree.
//
// What it is for. The class it exists to catch, measured over 48 h on the
// Qiddiya project: a viewer on a UDP relay whose video packets are lost in
// transit — 50–110 kbps trickling in (audio + fragments), 63k–178k packets
// lost, zero frames for 10–27 minutes, while the SAME office on the SAME path
// streams cleanly an hour later. Codec was ruled out (same AV1 both ways). A
// fresh session preferring TCP relay is the remedy; this module only decides
// WHEN, and never acts by itself — the caller owns the action.
//
// Deliberately pure: no React, no DOM lookups of its own, no timers unless
// start() is called. Every input is injected so the classifier can be driven
// by recorded snapshots in tests.

export const TICK_MS = 2000;
// Ticks a class must persist before it is a verdict (5 × 2 s = 10 s).
export const VERDICT_TICKS = 5;
// Nothing is judged this soon after ICE connected: the streamer may still be
// finishing its first frames.
export const WARMUP_MS = 10000;
// Frozen-after-start needs a longer window than the pre-first-frame classes:
// a static scene legitimately sends very little.
export const FROZEN_MS = 15000;
// Below this many packets per tick the path is not "carrying media", so lossy
// cannot be judged (it would be no-media or startup instead).
export const MIN_PACKETS_PER_TICK = 20;
// Share of packets lost, over the verdict window, that marks a lossy path.
export const LOSSY_SHARE = 0.4;

export const CLASS = Object.freeze({
  HEALTHY: 'healthy',
  NO_MEDIA: 'no-media',      // connected, zero bytes ever arrived
  LOSSY_PATH: 'lossy-path',  // packets arrive, most are lost, no frame ever
  FROZEN: 'frozen',          // had frames, then none while packets still arrive
  WAITING: 'waiting',        // too early / guarded / nothing conclusive
});

/**
 * Pick the inbound video report that carries the stream. mediasoup's bandwidth
 * probe ("probator") and a dead receiver left behind by a reconnect are also
 * inbound-rtp video reports; "last one wins" is how SDK v2 misreads meetings.
 */
export function pickInboundVideo(statsReport) {
  let best = null;
  if (!statsReport || typeof statsReport.forEach !== 'function') return best;
  statsReport.forEach((r) => {
    if (!r || r.type !== 'inbound-rtp') return;
    if (r.kind !== 'video' && r.mediaType !== 'video') return;
    if (r.trackIdentifier === 'probator') return;
    if (!best || (r.packetsReceived || 0) > (best.packetsReceived || 0)) best = r;
  });
  return best;
}

/**
 * Read the element's decoded-frame count. Chromium and Firefox expose
 * getVideoPlaybackQuality; older WebKit exposes webkitDecodedFrameCount.
 * null = this browser gives no count (the caller then relies on
 * requestVideoFrameCallback, or on the track state).
 */
export function readElementFrames(video) {
  try {
    if (video && typeof video.getVideoPlaybackQuality === 'function') {
      const q = video.getVideoPlaybackQuality();
      if (q && typeof q.totalVideoFrames === 'number') return q.totalVideoFrames;
    }
    if (video && typeof video.webkitDecodedFrameCount === 'number') return video.webkitDecodedFrameCount;
  } catch (_) { /* fall through */ }
  return null;
}

/**
 * Classify one verdict window. `ticks` is the last VERDICT_TICKS snapshots,
 * oldest first; each has { framesAdvanced, packetsDelta, lostDelta, bytesDelta }.
 * `ctx` carries what the window cannot see on its own.
 */
export function classifyWindow(ticks, ctx) {
  const { sinceConnectedMs, everHadFrames, hidden, playRejected, framesStaleMs } = ctx;
  if (hidden || playRejected) return CLASS.WAITING;
  if (!ticks.length) return CLASS.WAITING;
  if (ticks.some((t) => t.framesAdvanced)) return CLASS.HEALTHY;
  if (sinceConnectedMs < WARMUP_MS) return CLASS.WAITING;

  const packets = ticks.reduce((s, t) => s + (t.packetsDelta || 0), 0);
  const lost = ticks.reduce((s, t) => s + (t.lostDelta || 0), 0);
  const bytes = ticks.reduce((s, t) => s + (t.bytesDelta || 0), 0);
  const full = ticks.length >= VERDICT_TICKS;

  if (everHadFrames) {
    // Frames flowed once. Only call it frozen after a long stale window AND
    // while packets are still arriving — a paused/ended track is not "frozen",
    // and a static scene that stopped sending is not a fault of the path.
    if (framesStaleMs >= FROZEN_MS && packets >= MIN_PACKETS_PER_TICK) return CLASS.FROZEN;
    return CLASS.WAITING;
  }
  if (!full) return CLASS.WAITING;
  if (bytes === 0 && packets === 0) return CLASS.NO_MEDIA;
  const perTick = packets / ticks.length;
  const share = packets + lost > 0 ? lost / (packets + lost) : 0;
  if (perTick >= MIN_PACKETS_PER_TICK && share >= LOSSY_SHARE) return CLASS.LOSSY_PATH;
  return CLASS.WAITING;
}

/**
 * createMediaHealthMonitor
 *
 * deps:
 *   getVideoElement()   -> HTMLVideoElement | null
 *   getPeerConnection() -> RTCPeerConnection | null
 *   isHidden()          -> boolean            (document.hidden)
 *   getFlags()          -> { playRejected, videoInitialized }
 *   now()               -> ms                 (injectable for tests)
 *   onVerdict(cls, snapshot)   fired ONCE per class per monitor lifetime
 *   onRecovered(cls, snapshot) fired when healthy follows a verdict
 *   log(msg)            optional
 */
export function createMediaHealthMonitor(deps) {
  const now = deps.now || (() => Date.now());
  const connectedAt = now();
  const ticks = [];
  const fired = new Set();
  let lastVerdict = null;
  let timer = null;
  let prev = null;            // previous inbound snapshot
  let lastFrames = null;      // last element frame count
  let lastFrameAt = connectedAt;
  let everHadFrames = false;
  let trackedEl = null;
  let last = null;            // last snapshot, for outcome reporting

  const attachFrameCallback = (ve) => {
    if (!ve || ve === trackedEl) return;
    trackedEl = ve;
    lastFrames = null;
    if (typeof ve.requestVideoFrameCallback === 'function') {
      const onFrame = () => {
        if (trackedEl !== ve) return;
        lastFrameAt = now();
        everHadFrames = true;
        try { ve.requestVideoFrameCallback(onFrame); } catch (_) { /* ignore */ }
      };
      try { ve.requestVideoFrameCallback(onFrame); } catch (_) { /* ignore */ }
    }
  };

  async function tick() {
    const t = now();
    const ve = deps.getVideoElement ? deps.getVideoElement() : null;
    attachFrameCallback(ve);

    // Frame signal: element counter first, the callback timestamp second.
    let framesAdvanced = false;
    const frames = readElementFrames(ve);
    if (frames != null) {
      if (lastFrames != null && frames > lastFrames) framesAdvanced = true;
      if (lastFrames == null && frames > 0) framesAdvanced = true;
      lastFrames = frames;
      if (framesAdvanced) { lastFrameAt = t; everHadFrames = true; }
    } else if (t - lastFrameAt < TICK_MS * 1.5 && everHadFrames) {
      framesAdvanced = true; // requestVideoFrameCallback fired since last tick
    }

    // Transport signal.
    let inbound = null;
    const pc = deps.getPeerConnection ? deps.getPeerConnection() : null;
    if (pc && typeof pc.getStats === 'function') {
      const stats = await pc.getStats().catch(() => null);
      inbound = pickInboundVideo(stats);
    }
    const cur = inbound ? {
      packets: inbound.packetsReceived || 0,
      lost: inbound.packetsLost || 0,
      bytes: inbound.bytesReceived || 0,
      framesDecoded: inbound.framesDecoded ?? null,
    } : { packets: 0, lost: 0, bytes: 0, framesDecoded: null };
    const delta = prev ? {
      packetsDelta: Math.max(0, cur.packets - prev.packets),
      lostDelta: Math.max(0, cur.lost - prev.lost),
      bytesDelta: Math.max(0, cur.bytes - prev.bytes),
    } : { packetsDelta: 0, lostDelta: 0, bytesDelta: 0 };
    prev = cur;

    const flags = deps.getFlags ? deps.getFlags() : {};
    const hidden = deps.isHidden ? !!deps.isHidden() : false;
    if (hidden) { ticks.length = 0; lastFrameAt = t; return CLASS.WAITING; }

    ticks.push({ framesAdvanced, ...delta });
    while (ticks.length > VERDICT_TICKS) ticks.shift();

    const ctx = {
      sinceConnectedMs: t - connectedAt,
      everHadFrames,
      hidden,
      playRejected: !!flags.playRejected,
      framesStaleMs: t - lastFrameAt,
    };
    const cls = classifyWindow(ticks, ctx);
    last = {
      class: cls,
      sinceConnectedMs: ctx.sinceConnectedMs,
      framesStaleMs: ctx.framesStaleMs,
      everHadFrames,
      elementFrames: lastFrames,
      frameSignal: frames != null ? 'element' : (typeof (ve && ve.requestVideoFrameCallback) === 'function' ? 'rvfc' : 'none'),
      packetsReceived: cur.packets,
      packetsLost: cur.lost,
      bytesReceived: cur.bytes,
      statsFramesDecoded: cur.framesDecoded,
      windowPackets: ticks.reduce((s, x) => s + x.packetsDelta, 0),
      windowLost: ticks.reduce((s, x) => s + x.lostDelta, 0),
      windowBytes: ticks.reduce((s, x) => s + x.bytesDelta, 0),
      videoInitialized: !!flags.videoInitialized,
    };

    if (cls === CLASS.HEALTHY) {
      if (lastVerdict && lastVerdict !== CLASS.HEALTHY && deps.onRecovered) deps.onRecovered(lastVerdict, last);
      lastVerdict = CLASS.HEALTHY;
      return cls;
    }
    if (cls === CLASS.WAITING) return cls;
    if (!fired.has(cls)) {
      fired.add(cls);
      lastVerdict = cls;
      if (deps.log) deps.log(`[mediaHealth] ${cls}`);
      if (deps.onVerdict) deps.onVerdict(cls, last);
    }
    return cls;
  }

  return {
    start(intervalMs = TICK_MS) {
      this.stop();
      timer = setInterval(() => { tick().catch(() => {}); }, intervalMs);
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      trackedEl = null;
    },
    tick,
    getLastSnapshot: () => last,
    hasFired: (cls) => fired.has(cls),
  };
}
